import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { pointerOutsideOfPreview } from '@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import { ThemeProvider, useTheme } from '@mui/material';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createRoot } from 'react-dom/client';
import { DragPreviewChip } from './dragPreview';
import type { DragData } from './types';

export const DRAG_PAYLOAD_KEY = 'enhancedai/sidebar-dnd';

export interface DragPayload {
  drag: DragData;
  label: string;
}

interface UseDraggableNodeArgs {
  data: DragData;
  label: string;
  disabled?: boolean;
}

export const useDraggableNode = ({
  data,
  label,
  disabled,
}: UseDraggableNodeArgs) => {
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const payloadRef = useRef<DragPayload>({ drag: data, label });
  const disabledRef = useRef<boolean>(!!disabled);
  const theme = useTheme();
  const themeRef = useRef(theme);

  useLayoutEffect(() => {
    payloadRef.current = { drag: data, label };
    disabledRef.current = !!disabled;
    themeRef.current = theme;
  }, [data, label, disabled, theme]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: pragmatic reads via refs; rebind only on element change.
  useEffect(() => {
    if (!element) return;
    return draggable({
      element,
      canDrag: () => !disabledRef.current,
      getInitialData: () => ({ [DRAG_PAYLOAD_KEY]: payloadRef.current }),
      onGenerateDragPreview: ({ nativeSetDragImage }) => {
        setCustomNativeDragPreview({
          nativeSetDragImage,
          getOffset: pointerOutsideOfPreview({ x: '8px', y: '8px' }),
          render: ({ container }) => {
            const root = createRoot(container);
            root.render(
              <ThemeProvider theme={themeRef.current}>
                <DragPreviewChip label={payloadRef.current.label} />
              </ThemeProvider>,
            );
            return () => root.unmount();
          },
        });
      },
      onDragStart: () => setIsDragging(true),
      onDrop: () => setIsDragging(false),
    });
  }, [element]);

  const setRef = useCallback((node: HTMLElement | null) => {
    setElement(node);
  }, []);

  return { setRef, isDragging };
};

export const extractDragPayload = (
  data: Record<string | symbol, unknown>,
): DragPayload | null => {
  const raw = data[DRAG_PAYLOAD_KEY];
  if (typeof raw !== 'object' || raw === null) return null;
  const candidate = raw as { drag?: unknown; label?: unknown };
  if (typeof candidate.drag !== 'object' || candidate.drag === null)
    return null;
  if (typeof (candidate.drag as { kind?: unknown }).kind !== 'string')
    return null;
  if (candidate.label !== undefined && typeof candidate.label !== 'string')
    return null;
  return candidate as DragPayload;
};
